import * as React from 'react';
import { Well } from '@pushly/aqe/lib/components';
import { getClassNames } from '../../../_utils/classnames';
import { Skeleton, Switch } from 'antd';
import './styles/notification-channel-selector.scss';
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS } from '../../../constants';
import { Container } from 'typescript-ioc';
import { AppState } from '../../../stores/app';
import { DomainDto } from '../../../dtos/domain';
import { FeatureDisplayWrapper } from '../../feature-display-wrapper/feature-display-wrapper';
import { IChannelState, INotificationChannels } from '../interfaces/delivery-channels.interface';
import { DeliveryChannel } from '@pushly/aqe/lib/interfaces/notification-channels';
import { AndroidFilled, AppleFilled, ChromeFilled } from '@ant-design/icons';

interface INotificationChannelSelector {
    loading?: boolean,
    value: INotificationChannels,
    onChange: (change: IChannelState) => void,
    domain: DomainDto,
}

export const NotificationChannelSelector = (props: INotificationChannelSelector) => {
    const { loading, value, domain } = props;


    const appState = Container.get(AppState);

    const nativeIOS = appState?.flags.findActive(FEAT_CHANNEL_IOS)?.getKey();
    const hasNativeIOSEnabled = props.domain?.flags.includes(nativeIOS!);
    const nativeAndroid = appState?.flags.findActive(FEAT_CHANNEL_ANDROID)?.getKey();
    const hasNativeAndroidEnabled = props.domain?.flags.includes(nativeAndroid!);

    const iosEnabled = hasNativeIOSEnabled && domain.nativeApnsConfiguration?.isActive;
    const androidEnabled = hasNativeAndroidEnabled && domain.nativeFcmConfiguration?.isActive;

    const enabledChannels = value ? Object?.keys(value)?.filter(key => value[key] === true) as DeliveryChannel[] : []
    if (enabledChannels.length < 1) {
        value[DeliveryChannel.WEB] = true;
    }

    let disableToggle: DeliveryChannel | undefined;
    if (enabledChannels.length === 1) {
        disableToggle = enabledChannels[0];
    }

    return (
        <Well
            className={getClassNames('notification-channel-selector', 'nested')}
            title="Delivery Channels"
            hideFooter={true}
        >
            <Skeleton
                loading={loading}
                active={true}
                avatar={false}
                title={false}
            >
                <Well
                    mode="ghost"
                    hideHeader={true}
                    hideFooter={true}
                >
                    <div className="channel-selectors">
                        <ChannelSelector
                            value={value[DeliveryChannel.WEB]}
                            channel={DeliveryChannel.WEB}
                            onChange={props.onChange}
                            disabledToggle={disableToggle === DeliveryChannel.WEB}
                        />
                        {iosEnabled && (
                            <ChannelSelector
                                value={value[DeliveryChannel.NATIVE_IOS]}
                                channel={DeliveryChannel.NATIVE_IOS}
                                onChange={props.onChange}
                                disabled={!iosEnabled}
                                disabledToggle={disableToggle === DeliveryChannel.NATIVE_IOS}
                            />
                        )}

                        {androidEnabled && (
                            <ChannelSelector
                                value={value[DeliveryChannel.NATIVE_ANDROID]}
                                channel={DeliveryChannel.NATIVE_ANDROID}
                                onChange={props.onChange}
                                disabled={!androidEnabled}
                                disabledToggle={disableToggle === DeliveryChannel.NATIVE_ANDROID}
                            />
                        )}
                    </div>
                </Well>

            </Skeleton>

        </Well>
    );
}

interface IChannelSelector {
    className?: string,
    value: boolean,
    channel: DeliveryChannel,
    onChange: (change: IChannelState) => void,
    disabled?: boolean,
    disabledToggle?: boolean,
    label?: string,
}

const FragmentWrapper = (_: any) => _.children;

export const ChannelSelector = (props: IChannelSelector) => {
    const { value, disabled, disabledToggle, onChange, channel, className } = props;
    let label = props.label;
    let icon;

    const DisplayWrapper = disabled ? FeatureDisplayWrapper : FragmentWrapper;

    if (!label) {
        switch(channel) {
            case DeliveryChannel.WEB:
                label ='Web';
                icon = <ChromeFilled />;
                break;
            case DeliveryChannel.NATIVE_IOS:
                label ='Native: iOS';
                icon = <AppleFilled />;
                break;
            case DeliveryChannel.NATIVE_ANDROID:
                label ='Native: Android';
                icon = <AndroidFilled />;
                break;
            default:
                break;
        }
    }

    return (
        <div
            className={`channel-selector-item ${className ?? ''}`}
        >
            <DisplayWrapper type={'tooltip'} forwardProps={{ title: 'To enable this selection please contact your Account Manager.'}}>
                <label>
                    <Switch
                        checked={value}
                        size="small"
                        onChange={(enabled) => {
                            if (!disabledToggle) {
                                onChange({ channel, enabled })
                            }
                        }}
                        disabled={disabled}
                    />
                    <span className="channel-selector-label">{icon} {label}</span>
                </label>
            </DisplayWrapper>
        </div>
    );
}
